import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, CalendarDaysIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'

const MESES = [
    { value: 'janeiro', label: 'Janeiro' },
    { value: 'fevereiro', label: 'Fevereiro' },
    { value: 'março', label: 'Março' },
    { value: 'abril', label: 'Abril' },
    { value: 'maio', label: 'Maio' },
    { value: 'junho', label: 'Junho' },
    { value: 'julho', label: 'Julho' },
    { value: 'agosto', label: 'Agosto' },
    { value: 'setembro', label: 'Setembro' },
    { value: 'outubro', label: 'Outubro' },
    { value: 'novembro', label: 'Novembro' },
    { value: 'dezembro', label: 'Dezembro' },
]

const anoAtual = new Date().getFullYear()
const ANOS = Array.from({ length: anoAtual - 1999 }, (_, i) => anoAtual - i)

const ModalDataExibicao = ({ open, onClose, onSave, color = 'yellow' }) => {
    const colorClasses = colorMap[color] || colorMap.yellow

    const [mes, setMes] = useState('')
    const [ano, setAno] = useState(String(anoAtual))
    const [enviarEmail, setEnviarEmail] = useState(false)
    const [errorMes, setErrorMes] = useState(false)
    const [errorAno, setErrorAno] = useState(false)

    useEffect(() => {
        if (!open) {
            setMes('')
            setAno(String(anoAtual))
            setEnviarEmail(false)
            setErrorMes(false)
            setErrorAno(false)
        }
    }, [open])

    const handleSubmit = () => {
        let error = false

        if (!mes) {
            setErrorMes(true)
            error = true
        } else setErrorMes(false)

        if (!ano) {
            setErrorAno(true)
            error = true
        } else setErrorAno(false)

        if (error) return

        onSave({
            mes,
            ano: String(ano),
            enviar_email: enviarEmail,
        })
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className={`flex h-12 w-12 items-center justify-center rounded-full ${colorClasses.headerIconBg} mr-4`}>
                                            <CalendarDaysIcon className={`h-6 w-6 ${colorClasses.headerIconText}`} />
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                                Nova data de exibição
                                            </Dialog.Title>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Selecione o mês e ano liberados para visualização do investidor
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="mes" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Mês
                                            </label>
                                            <select
                                                id="mes"
                                                value={mes}
                                                onChange={(e) => { setMes(e.target.value); setErrorMes(false) }}
                                                className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorMes
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : `${colorClasses.inputBorder} focus:ring-brand-yellow focus:border-brand-yellow`
                                                    } bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                            >
                                                <option value="" disabled>Selecione o mês</option>
                                                {MESES.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                            {errorMes && (
                                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                                    <span className="mr-1">⚠</span>
                                                    Campo obrigatório
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label htmlFor="ano" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Ano
                                            </label>
                                            <select
                                                id="ano"
                                                value={ano}
                                                onChange={(e) => { setAno(e.target.value); setErrorAno(false) }}
                                                className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorAno
                                                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                    : `${colorClasses.inputBorder} focus:ring-brand-yellow focus:border-brand-yellow`
                                                    } bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                            >
                                                <option value="" disabled>Selecione o ano</option>
                                                {ANOS.map((a) => (
                                                    <option key={a} value={String(a)}>{a}</option>
                                                ))}
                                            </select>
                                            {errorAno && (
                                                <p className="mt-2 text-sm text-red-600 flex items-center">
                                                    <span className="mr-1">⚠</span>
                                                    Campo obrigatório
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enviarEmail}
                                            onChange={(e) => setEnviarEmail(e.target.checked)}
                                            className="h-5 w-5 rounded border-gray-300 text-brand-yellow focus:ring-brand-yellow"
                                        />
                                        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                            <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                                            Notificar investidores por e-mail
                                        </span>
                                    </label>
                                </div>

                                <div className="mt-8 flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        className={`px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r ${colorClasses.buttonPrimary} ${colorClasses.buttonPrimaryHover} shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105`}
                                    >
                                        Cadastrar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalDataExibicao
